'use client'

import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { getStorageItem, setStorageItem, STORAGE_KEYS } from '@/lib/pwa-storage'
import { readCountryCookie } from '@/lib/country-cookie'
import { resolveDisplayCurrency } from '@/server/domain/display-currency'
import type { PriceCurrency } from '@/server/domain/pricing'

/**
 * Divisa en la que se ANUNCIA el catálogo. Es la de escaparate de `pricing.ts`
 * (ADR-021 regla 3), no la del cargo: lo que se cobra lo decide el producto del
 * proveedor y puede ser una tercera divisa.
 */
export type Currency = PriceCurrency

/**
 * `?currency=` de la URL, leída en el cliente.
 *
 * Las landings son estáticas (tráfico SEO), así que el servidor NO puede pasar
 * el query param sin volverlas dinámicas: la lee el navegador.
 */
function currencyFromLocation(): string | null {
  if (typeof window === 'undefined') return null

  return new URLSearchParams(window.location.search).get('currency')
}

/**
 * Store de la divisa: estado de módulo (por pestaña), no un recálculo a partir
 * de globals en cada lectura.
 *
 * `chosen` es la elección de ESTA pestaña y es la fuente de verdad de la UI:
 * `setStorageItem` devuelve `false` cuando el navegador no deja escribir (Safari
 * con «bloquear todas las cookies», almacenamiento lleno, WKWebView capado), y
 * sin este campo el botón se quedaba mudo — se escribía nada, se notificaba, y
 * la relectura devolvía otra vez el valor viejo.
 *
 * `cached` existe porque `getSnapshot` corre en **fase de render** y varias
 * veces por render: sin caché, cada snapshot hacía una lectura de localStorage
 * que además **escribe** (`isLocalStorageAvailable` prueba con un `setItem` +
 * `removeItem`), más un parseo de `document.cookie` y un `URLSearchParams`.
 *
 * En servidor nunca se toca: React usa `getServerSnapshot`, y el efecto y
 * `setCurrency` son de cliente.
 */
let chosen: Currency | null = null
let cached: { currency: Currency; persist: boolean } | null = null

function resolve(): { currency: Currency; persist: boolean } {
  cached ??= resolveDisplayCurrency({
    chosen: chosen ?? getStorageItem(STORAGE_KEYS.CURRENCY_PREFERENCE),
    fromUrl: currencyFromLocation(),
    country: readCountryCookie(),
  })

  return cached
}

/** Solo para tests: el store es estado de módulo y sobrevive entre casos. */
export function resetCurrencyPreferenceStore(): void {
  chosen = null
  cached = null
}

const listeners = new Set<() => void>()

function emitChange(): void {
  for (const listener of listeners) listener()
}

function subscribe(onStoreChange: () => void): () => void {
  /**
   * Otra pestaña cambió el valor duradero: gana el disco, así que se descarta
   * también la elección en memoria y las dos pestañas convergen.
   *
   * El wrapper se crea POR SUSCRIPTOR a propósito: `addEventListener` deduplica
   * referencias idénticas, así que con una función compartida el primer
   * `unsubscribe` desengancharía a todos los demás.
   */
  const onExternalChange = () => {
    chosen = null
    cached = null
    onStoreChange()
  }

  listeners.add(onStoreChange)
  window.addEventListener('storage', onExternalChange)

  return () => {
    listeners.delete(onStoreChange)
    window.removeEventListener('storage', onExternalChange)
  }
}

function getSnapshot(): Currency {
  return resolve().currency
}

/**
 * Snapshot del servidor: EUR fijo. El HTML servido no puede depender de
 * localStorage ni de la cookie sin dejar de ser el mismo para todos (que es lo
 * que mantiene las landings en el CDN), así que el servidor pinta la divisa del
 * mercado principal y el cliente ajusta.
 */
function getServerSnapshot(): Currency {
  return 'EUR'
}

/**
 * Divisa de escaparate. La precedencia y el "¿se guarda?" son del dominio
 * (`resolveDisplayCurrency`); aquí solo se leen las señales y se notifica.
 *
 * `useSyncExternalStore` y no `useState` + efecto porque es exactamente el caso
 * que esa API resuelve: un valor que solo existe en el cliente y cuyo snapshot
 * de servidor es distinto. Con un efecto, cada montaje pintaba EUR un frame
 * antes de aplicar la preferencia guardada — invisible en la landing estática,
 * pero visible al abrir el paywall tras navegar dentro de la app.
 */
export function useCurrencyPreference(): [Currency, (currency: Currency) => void] {
  const currency = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const setCurrency = useCallback((newCurrency: Currency) => {
    chosen = newCurrency
    setStorageItem(STORAGE_KEYS.CURRENCY_PREFERENCE, newCurrency)
    cached = null
    emitChange()
  }, [])

  // Único efecto: persistir la intención explícita de una campaña
  // (`?currency=`) la primera vez que se ve, para que sobreviva a la navegación.
  // El dominio decide si toca guardar; aquí solo se ejecuta.
  useEffect(() => {
    const resolved = resolve()
    if (resolved.persist) setCurrency(resolved.currency)
  }, [setCurrency])

  return [currency, setCurrency]
}
